/**
 * Client observability — WEB implementation (Grafana Faro).
 *
 * Metro resolves this on `Platform.OS === 'web'`; sentry.native.ts serves
 * iOS/Android. The two export an IDENTICAL surface, and that surface is
 * unchanged from the no-op stub this replaces — all ~35 existing call sites
 * (stores, the API client, error boundaries, _layout.tsx) keep working with no
 * edit. The file is still named `sentry.*` for the same reason: renaming it
 * would touch every one of those imports for no behavioural gain.
 *
 * Config arrives at RUNTIME from window.__CYNOSURE_CONFIG__, injected into
 * index.html by the backend at startup (backend/app/main.py). It is deliberately
 * NOT a build arg: one image is deployed to every cluster, so a build-time
 * endpoint would be wrong somewhere. With no config — local `expo start`, tests
 * — reporting stays off and the module falls back to console logging in dev.
 *
 * Events land in Loki via the fleet's ingest endpoint and are queryable as
 * `{source="faro", app_name="cynosure-user"}`. See a3rn-fleet
 * docs/observability.md.
 */

import {
  faro,
  FetchTransport,
  getWebInstrumentations,
  initializeFaro,
  LogLevel,
} from '@grafana/faro-web-sdk';

import {
  clearBreadcrumbs,
  pushBreadcrumb,
  recentBreadcrumbs,
  scrub,
  toFaroContext,
  type Breadcrumb,
} from './scrub';

export type { Breadcrumb };

export interface SentryConfig {
  release?: string;
  environment?: string;
  dist?: string;
}

interface SentryUser {
  id: string;
  email: string;
}

interface RuntimeConfig {
  environment?: string;
  faroUrl?: string;
  faroWriteKey?: string;
  faroAppName?: string;
  release?: string;
}

function runtimeConfig(): RuntimeConfig {
  if (typeof window === 'undefined') return {};
  return (window as unknown as { __CYNOSURE_CONFIG__?: RuntimeConfig }).__CYNOSURE_CONFIG__ ?? {};
}

let _initialised = false;
let _handlersInstalled = false;
let _user: SentryUser | null = null;

// Account-level diagnostics consent — settings.diagnostics_opt_in. See the
// longer note in sentry.native.ts: same contract, same reason it starts true.
// localStorage is synchronous here, so the web window between boot and the
// cached preference being applied is narrower than native's, but the two
// implementations keep identical behaviour rather than diverging.
let _diagnosticsEnabled = true;

function devLog(label: string, ...args: unknown[]): void {
  if (__DEV__) {
    // eslint-disable-next-line no-console
    console.warn(`[observability] ${label}`, ...args);
  }
}

export function initSentry(config?: SentryConfig): void {
  if (_initialised) return;
  const rc = runtimeConfig();

  // No endpoint configured — local dev, tests, or a cluster that has not set
  // FARO_URL. Install the dev handlers so errors are still visible locally, and
  // send nothing.
  if (!rc.faroUrl) {
    installGlobalHandlers();
    devLog('initSentry (reporting disabled — no faroUrl)', config);
    return;
  }

  initializeFaro({
    url: rc.faroUrl,
    apiKey: rc.faroWriteKey,
    // ⚠️  `keepalive` IS WHAT MAKES AN ERROR SURVIVE THE WINDOW THAT THREW IT.
    //     Faro's default FetchTransport does not set it, and an ordinary fetch
    //     is cancelled when its document goes away — so the events worth having
    //     most, the ones immediately before a window closes, are exactly the
    //     ones that never arrive. On 2026-08-04 a sign-in failure was visible
    //     on screen and absent from `{source="faro", kind="exception"}`
    //     entirely, because it was thrown in the OIDC popup and the popup
    //     closed. Constructing the transport by hand is the only way to reach
    //     the fetch options; everything else here is the default.
    //
    //     The 64KB cap browsers place on keepalive bodies is not a concern at
    //     this event size, and exceeding it would fail these sends the same way
    //     they already fail today.
    transports: [
      new FetchTransport({
        url: rc.faroUrl,
        apiKey: rc.faroWriteKey,
        requestOptions: { keepalive: true },
      }),
    ],
    app: {
      name: rc.faroAppName ?? 'cynosure-user',
      // The RUNTIME release wins over the caller's. _layout.tsx passes the Expo
      // bundle version, but on web the deployed chart appVersion is what
      // identifies "which deploy is this" — and that is the axis the
      // "exception rate by release" panel splits on.
      version: rc.release ?? config?.release ?? 'unknown',
      // 'unknown', not 'production' — see the note in admin's sentry.ts. An
      // unlabelled beacon counted as production is the one way a local run can
      // contaminate the prod dashboards, and `environment` is a JSON field
      // rather than a Loki label, so it cannot be filtered out cheaply after.
      environment: rc.environment ?? config?.environment ?? 'unknown',
    },
    sessionTracking: { enabled: true, persistent: true },
    instrumentations: [
      ...getWebInstrumentations({
        captureConsole: true,
        // OFF, and measured: with this on (its default), one event is emitted per
        // HTTP resource — 153 of 191 lines from a single browser session, ~80% of
        // total ingest, for data no dashboard panel queries. Same volume class as
        // the user-action spans deliberately excluded here. Web vitals come from a
        // separate instrumentation and are unaffected: LCP/FCP/TTFB still arrive as
        // kind="measurement".
        enablePerformanceInstrumentation: false,
      }),
      // NOTE: no TracingInstrumentation (@grafana/faro-web-tracing). There is no
      // Tempo in the fleet, and backend correlation rides on X-Request-Id
      // instead — see src/api/client.ts.
    ],
    // Drop the noise levels; keep warn/error/info. console.error is the leading
    // indicator that usually precedes a thrown exception, so it earns its
    // volume. (These are also Faro's defaults — stated explicitly so a future
    // default change doesn't silently start shipping every console.log.)
    consoleInstrumentation: {
      disabledLevels: [LogLevel.DEBUG, LogLevel.TRACE, LogLevel.LOG],
    },
    // Last line of defence before anything leaves the browser. The ingest side
    // masks emails too, but that is a backstop for what slips past here.
    beforeSend: (item) => scrub(item) as typeof item,
  });

  _initialised = true;
  _handlersInstalled = true; // Faro's error instrumentation owns the globals now.
  devLog('initSentry (Faro active)', { app: rc.faroAppName, release: rc.release });
}

/**
 * Apply the account's diagnostics preference. Mirror of the native
 * implementation — see sentry.native.ts for why this gates the transport rather
 * than reinitialising, and why the account ID is gated along with it.
 */
export function setDiagnosticsEnabled(enabled: boolean): void {
  const changed = _diagnosticsEnabled !== enabled;
  _diagnosticsEnabled = enabled;

  if (!_initialised) return;
  if (!changed) return;

  if (enabled) {
    faro?.unpause?.();
    if (_user) faro?.api?.setUser({ id: _user.id });
  } else {
    faro?.api?.resetUser();
    faro?.pause?.();
  }
  devLog('setDiagnosticsEnabled', { enabled });
}

/** The preference currently in force. */
export function isDiagnosticsEnabled(): boolean {
  return _diagnosticsEnabled;
}

export function setSentryUser(user: SentryUser): void {
  _user = user;
  // id ONLY. The email is deliberately withheld: it is the single most
  // identifying field we hold, it is not needed to group or trace an error, and
  // the backend masks it in its own logs — sending it here would make the
  // client stream the least private of the two.
  // Withheld entirely while opted out — setDiagnosticsEnabled re-attaches it.
  if (!_diagnosticsEnabled) {
    devLog('setSentryUser (withheld — diagnostics off)', { id: user.id });
    return;
  }
  faro?.api?.setUser({ id: user.id });
  devLog('setSentryUser', { id: user.id });
}

export function clearSentryUser(): void {
  _user = null;
  clearBreadcrumbs();
  faro?.api?.resetUser();
  devLog('clearSentryUser');
}

export function captureException(error: unknown, extra?: Record<string, unknown>): void {
  const err = error instanceof Error ? error : new Error(String(error));

  if (!_initialised) {
    devLog('captureException (not reporting)', err, { extra, user: _user?.id ?? null });
    return;
  }

  const crumbs = recentBreadcrumbs();
  faro.api.pushError(err, {
    context: {
      ...toFaroContext(extra),
      // Attached to the error rather than shipped as their own events — see the
      // note in scrub.ts on why breadcrumbs are error-scoped here.
      ...(crumbs.length ? { breadcrumbs: JSON.stringify(crumbs) } : {}),
    },
  });
  devLog('captureException', err, extra);
}

export function setRelease(release: string, dist?: string): void {
  // Faro fixes app metadata at initialisation, so this cannot re-tag the app
  // after the fact. Recorded as an event so a mid-session release change (an
  // OTA update) is at least visible on the timeline.
  faro?.api?.pushEvent('release_changed', { release, ...(dist ? { dist } : {}) });
  devLog('setRelease', { release, dist });
}

/**
 * Tag the session with whether this build is still the released one, so an
 * alert can decline to page on errors from clients nobody can fix.
 *
 * A SESSION attribute rather than error context, because that is what reaches
 * every event kind — Faro's own instrumented errors included, which never pass
 * through captureException. Confirmed against the live stream: Faro's built-in
 * `previousSession` attribute arrives in Loki as `session_attr_previousSession`,
 * so this lands as `session_attr_versionStatus`.
 *
 * Faro fixes session state at set time and this can only be known after
 * /api/v1/meta answers, so events emitted during boot carry NO such field. Any
 * query that consumes it must therefore exclude "outdated" rather than require
 * "current" — the missing case is a real, unremarkable startup error, not
 * something to hide.
 *
 * The existing attributes are spread back in so Faro's own session tracking
 * (previousSession) survives.
 */
export function setVersionStatus(status: string): void {
  const session = faro?.api?.getSession();
  faro?.api?.setSession({
    ...session,
    attributes: { ...session?.attributes, versionStatus: status },
  });
  devLog('setVersionStatus', { status });
}

export function setTag(key: string, value: string): void {
  faro?.api?.pushEvent('tag', { [key]: String(value) });
}

export function setContext(name: string, context: Record<string, unknown>): void {
  faro?.api?.pushEvent(`context.${name}`, toFaroContext(context));
}

export function addBreadcrumb(breadcrumb: Breadcrumb): void {
  pushBreadcrumb(breadcrumb);
}

export function installGlobalHandlers(): void {
  // When Faro is active its error instrumentation already owns window.onerror
  // and unhandledrejection; adding ours would double-report every crash.
  if (_handlersInstalled) return;
  if (typeof window === 'undefined') return;
  _handlersInstalled = true;

  window.addEventListener('error', (ev: ErrorEvent) => {
    captureException(ev.error ?? ev.message, {
      context: 'globalHandler.windowError',
      filename: ev.filename,
      lineno: ev.lineno,
      colno: ev.colno,
    });
  });
  window.addEventListener('unhandledrejection', (ev: PromiseRejectionEvent) => {
    captureException(ev.reason, { context: 'globalHandler.unhandledRejection' });
  });
}
